/**
 * KAN-735: отчёт о расходах за месяц, выбор месяца и сохранение курса доллара.
 */
import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { currentMonth, isExpensesReport, shiftMonth } from '@/lib/expenses';
import { expensesApi } from '@/services/api/expenses';
import { EXPENSES_QUERY_KEY } from '@/services/api/queryKeys';
import type { ExpensesReport } from '@/types/expenses';

/** enabled=false: не загружать (например, не владельцу). */
export function useExpenses(month: string, enabled = true) {
  return useQuery<ExpensesReport>({
    queryKey: [...EXPENSES_QUERY_KEY, month],
    queryFn: () => expensesApi.get(month),
    staleTime: 60_000,
    enabled,
  });
}

/** Отчёт, если ответ действительно отчёт; иначе null (плашка не ломает дашборд). */
export function useSafeExpenses(month: string, enabled: boolean): ExpensesReport | null {
  const { data, isError } = useExpenses(month, enabled);
  return !isError && isExpensesReport(data) ? data : null;
}

/** Месяц страницы: с текущего, назад без ограничений, вперёд не дальше текущего. */
export function useMonthNavigation() {
  const [month, setMonth] = useState(() => currentMonth());
  const canGoNext = month < currentMonth();
  const prev = useCallback(() => setMonth((m) => shiftMonth(m, -1)), []);
  const next = useCallback(
    () => setMonth((m) => (m < currentMonth() ? shiftMonth(m, 1) : m)),
    []
  );
  return { month, prev, next, canGoNext };
}

/** PATCH курса; после сохранения перечитываются все отчёты (суммы в ₽ зависят от курса). */
export function useUpdateUsdRate() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (rate: number) => expensesApi.updateRate(rate),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: EXPENSES_QUERY_KEY });
    },
  });
}
